import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import Select from 'react-select';
import { FileText, Tags, Trash2, X, CheckCircle, Plus, Edit3, Hash } from 'lucide-react';
import { useDocumentCategories } from '../../contexts/DocumentCategoriesContext';
import ConfirmationModal from '../../Components/ConfirmationModal';

interface TextDocumentEditorProps {
    visible: boolean;
    onSave: (title: string, content: string, category: string) => void;
    onCancel: () => void;
    onDelete?: () => void;
    isEditMode?: boolean;
    initialData?: {
        title: string;
        content: string;
        category: string;
    };
}

export const TextDocumentEditor: React.FC<TextDocumentEditorProps> = ({
                                                                          visible,
                                                                          onSave,
                                                                          onCancel,
                                                                          onDelete,
                                                                          isEditMode = false,
                                                                          initialData
                                                                      }) => {
    const { categories } = useDocumentCategories();
    const categoryOptions = useMemo(() => categories.map(c => ({ value: c.key, label: c.label })), [categories]);

    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(categoryOptions[0]);
    const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    useEffect(() => {
        if (categoryOptions.length > 0 && !selectedCategory) {
            setSelectedCategory(categoryOptions[0]);
        }
    }, [categoryOptions]);

    useEffect(() => {
        if (initialData) {
            setTitle(initialData.title);
            setContent(initialData.content);
            const category = categoryOptions.find(cat => cat.value === initialData.category);
            if (category) setSelectedCategory(category);
        } else {
            // Reset for new document
            setTitle('');
            setContent('');
            setSelectedCategory(categoryOptions[0]);
        }
    }, [initialData, visible]);

    if (!visible) return null;

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (title.trim() && selectedCategory) {
            onSave(title.trim(), content, selectedCategory.value);
        }
    };

    const hasUnsavedChanges = (): boolean => {
        if (isEditMode && initialData) {
            // Only count category as changed if the original category still exists as an option
            const categoryChanged = categoryOptions.some(c => c.value === initialData.category)
                && selectedCategory?.value !== initialData.category;
            return title !== initialData.title || content !== initialData.content || categoryChanged;
        }
        return title.trim() !== '' || content.trim() !== '';
    };

    const handleCancel = () => {
        if (hasUnsavedChanges()) {
            setShowDiscardConfirm(true);
            return;
        }
        onCancel();
    };

    const handleConfirmDiscard = () => {
        setShowDiscardConfirm(false);
        onCancel();
    };

    const handleDelete = () => {
        if (!onDelete) return;
        setShowDeleteConfirm(true);
    };

    const handleConfirmDelete = () => {
        setShowDeleteConfirm(false);
        onDelete?.();
    };

    const wordCount = content.trim().split(/\s+/).filter(word => word.length > 0).length;

    // Portal to body: .main-content creates a stacking context (z-index: 1),
    // so a modal rendered inside it would paint below the sidebar.
    return createPortal(
        <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-xl modal-dialog-centered modal-fullscreen-lg-down">
                <div className="modal-content" style={{ height: "85vh" }}>
                    <div className="modal-header">
                        <h5 className="modal-title">
                            {isEditMode ? (
                                <Edit3 className="me-2" size={18} />
                            ) : (
                                <FileText className="me-2" size={18} />
                            )}
                            {isEditMode ? "Redigera textdokument" : "Skapa textdokument"}
                        </h5>
                    </div>

                    <form onSubmit={handleSubmit} className="d-flex flex-column h-100">
                        <div className="modal-body flex-grow-1 d-flex flex-column">
                            <div className="row mb-3">
                                <div className="col-md-8">
                                    <label htmlFor="documentTitle" className="form-label">
                                        <FileText className="me-1" size={14} />
                                        Titel
                                    </label>
                                    <input
                                        id="documentTitle"
                                        type="text"
                                        className="form-control"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        placeholder="Dokumentets titel"
                                        required
                                    />
                                </div>

                                <div className="col-md-4">
                                    <label className="form-label">
                                        <Tags className="me-1" size={14} />
                                        Kategori
                                    </label>
                                    <Select
                                        options={categoryOptions}
                                        value={selectedCategory}
                                        onChange={(val) => {
                                            if (val) setSelectedCategory(val);
                                        }}
                                    />
                                </div>
                            </div>

                            <div className="flex-grow-1 d-flex flex-column">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <label htmlFor="documentContent" className="form-label mb-0">
                                        <Edit3 className="me-1" size={14} />
                                        Innehåll
                                    </label>
                                    <span className="badge bg-secondary">
                                        <Hash className="me-1" size={12} />
                                        {wordCount} ord
                                    </span>
                                </div>

                                <textarea
                                    id="documentContent"
                                    className="form-control flex-grow-1"
                                    value={content}
                                    onChange={(e) => setContent(e.target.value)}
                                    placeholder="Skriv din text här..."
                                    style={{
                                        fontFamily: "inherit",
                                        fontSize: "16px",
                                        resize: "none",
                                        minHeight: "150px",
                                        lineHeight: "1.5"
                                    }}
                                />
                            </div>
                        </div>

                        <div className="modal-footer">
                            {isEditMode && onDelete && (
                                <button
                                    type="button"
                                    className="btn btn-outline-danger me-auto"
                                    onClick={handleDelete}
                                >
                                    <Trash2 className="me-1" size={14} />
                                    Radera
                                </button>
                            )}

                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleCancel}
                            >
                                <X className="me-1" size={14} />
                                Avbryt
                            </button>

                            <button
                                type="submit"
                                className="btn btn-primary"
                            >
                                {isEditMode ? (
                                    <CheckCircle className="me-1" size={14} />
                                ) : (
                                    <Plus className="me-1" size={14} />
                                )}
                                {isEditMode ? "Spara ändringar" : "Skapa dokument"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <ConfirmationModal
                show={showDiscardConfirm}
                title="Osparade ändringar"
                message="Du har osparade ändringar. Vill du stänga utan att spara?"
                confirmText="Släng ändringar"
                cancelText="Fortsätt redigera"
                variant="danger"
                size="md"
                onConfirm={handleConfirmDiscard}
                onCancel={() => setShowDiscardConfirm(false)}
            />

            <ConfirmationModal
                show={showDeleteConfirm}
                title="Radera textdokument"
                message="Är du säker på att du vill radera detta textdokument?"
                confirmText="Radera"
                cancelText="Avbryt"
                variant="danger"
                size="md"
                onConfirm={handleConfirmDelete}
                onCancel={() => setShowDeleteConfirm(false)}
            />
        </div>,
        document.body
    );
};